import { getAttachmentFile } from '../components/ai-helper';

export class JustRecordWidget extends HTMLElement {
    private baseApi = 'https://justrecordme-proxy.gvtechhub.workers.dev';
    private attachmentId: string | null = null;
    private jobId: string | null = null;
    
    private actionArea!: HTMLDivElement;
    private statusEl!: HTMLDivElement;
    private progressBar!: HTMLDivElement;
    private progressFill!: HTMLDivElement;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.render();
    }

    private ytUrl: string | null = null;
    private ytApi = 'https://api-export.kaywat.codes';

    static get observedAttributes() {
        return ['attachment-id', 'yt-url'];
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (name === 'attachment-id' && newValue) {
            this.attachmentId = newValue;
            this.startTranscription();
        } else if (name === 'yt-url' && newValue) {
            this.ytUrl = newValue;
            this.startYtTranscription();
        }
    }

    render() {
        this.shadowRoot!.innerHTML = `
            <style>
                :host {
                    display: block;
                    width: 100%;
                    max-width: 500px;
                    margin: 10px 0;
                    font-family: var(--font-family, system-ui, -apple-system, sans-serif);
                }
                .widget-card {
                    background: #1e1f22;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.2);
                }
                .header {
                    background: #2b2d31;
                    padding: 12px 16px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
                }
                .logo {
                    width: 24px;
                    height: 24px;
                    background: #64b5f6;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    font-size: 12px;
                }
                .title {
                    color: #fff;
                    font-weight: 600;
                    font-size: 14px;
                }
                .body {
                    padding: 16px;
                }
                .status-text {
                    color: #b5bac1;
                    font-size: 14px;
                    margin-bottom: 12px;
                }
                .progress-bar {
                    width: 100%;
                    height: 8px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 4px;
                    overflow: hidden;
                    display: none;
                    margin-bottom: 12px;
                }
                .progress-fill {
                    width: 0%;
                    height: 100%;
                    background: #64b5f6;
                    transition: width 0.3s ease;
                }
                .action-area {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                .transcript-box {
                    background: rgba(0, 0, 0, 0.2);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    border-radius: 8px;
                    padding: 12px;
                    color: #dbdee1;
                    font-size: 14px;
                    line-height: 1.5;
                    max-height: 200px;
                    overflow-y: auto;
                    white-space: pre-wrap;
                }
                .download-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    background: #64b5f6;
                    color: #fff;
                    border: none;
                    border-radius: 6px;
                    padding: 8px 16px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    text-decoration: none;
                    transition: opacity 0.2s;
                }
                .download-btn:hover {
                    opacity: 0.9;
                }
            </style>
            <div class="widget-card">
                <div class="header">
                    <div class="logo"><i class="fas fa-microphone"></i></div>
                    <div class="title">JustRecord Transcription</div>
                </div>
                <div class="body">
                    <div class="status-text" id="statusText">Waiting for file...</div>
                    <div class="progress-bar" id="progressBar">
                        <div class="progress-fill" id="progressFill"></div>
                    </div>
                    <div class="action-area" id="actionArea"></div>
                </div>
            </div>
        `;
        
        this.statusEl = this.shadowRoot!.getElementById('statusText') as HTMLDivElement;
        this.progressBar = this.shadowRoot!.getElementById('progressBar') as HTMLDivElement;
        this.progressFill = this.shadowRoot!.getElementById('progressFill') as HTMLDivElement;
        this.actionArea = this.shadowRoot!.getElementById('actionArea') as HTMLDivElement;
    }

    async startTranscription() {
        if (!this.attachmentId) return;
        
        const file = getAttachmentFile(this.attachmentId);
        if (!file) {
            this.statusEl.textContent = 'Error: Attached file not found.';
            return;
        }

        this.statusEl.textContent = 'Uploading media...';
        this.progressBar.style.display = 'block';
        this.progressFill.style.width = '10%';
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const res = await fetch(this.baseApi + '/api/transcribe/async', {
                method: 'POST',
                body: formData
            });
            
            const data = await res.json();
            if (data.error) throw new Error(data.error);
            
            this.jobId = data.job_id;
            this.pollStatus();
        } catch (err: any) {
            this.statusEl.textContent = 'Failed: ' + err.message;
            this.progressBar.style.display = 'none';
        }
    }

    async startYtTranscription() {
        if (!this.ytUrl) return;

        this.statusEl.textContent = 'Extracting audio from YouTube...';
        this.progressBar.style.display = 'block';
        this.progressFill.style.width = '10%';

        try {
            // 1. Ask Export-YT to download audio
            const res = await fetch(this.ytApi + '/download-audio', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: this.ytUrl })
            });
            const data = await res.json();
            if (data.error) throw new Error(data.error);

            this.statusEl.textContent = 'Downloading audio to client...';
            this.progressFill.style.width = '30%';

            // 2. Fetch the actual file blob
            const filename = data.data?.filename || data.filename || data.file_name || data.data?.file_name;
            const fileRes = await fetch(this.ytApi + '/exports/' + filename);
            if (!fileRes.ok) throw new Error('Failed to fetch audio file');
            const blob = await fileRes.blob();
            const file = new File([blob], filename, { type: blob.type || 'audio/mp3' });

            // 3. Upload to JustRecord
            this.statusEl.textContent = 'Uploading to transcription server...';
            this.progressFill.style.width = '50%';

            const formData = new FormData();
            formData.append('file', file);

            const jrRes = await fetch(this.baseApi + '/api/transcribe/async', {
                method: 'POST',
                body: formData
            });

            const jrData = await jrRes.json();
            if (jrData.error) throw new Error(jrData.error);

            this.jobId = jrData.job_id;
            this.pollStatus();
        } catch (err: any) {
            this.statusEl.textContent = 'Failed: ' + err.message;
            this.progressBar.style.display = 'none';
        }
    }

    async pollStatus() {
        if (!this.jobId) return;
        
        try {
            const res = await fetch(this.baseApi + '/api/jobs/' + this.jobId);
            const data = await res.json();
            
            if (data.status === 'processing' || data.status === 'starting') {
                this.statusEl.textContent = data.message || 'Processing...';
                if (data.percent) {
                    this.progressFill.style.width = Math.max(10, data.percent) + '%';
                }
                setTimeout(() => this.pollStatus(), 3000);
            } else if (data.status === 'complete') {
                this.progressFill.style.width = '100%';
                this.statusEl.textContent = 'Transcription complete!';
                setTimeout(() => {
                    this.progressBar.style.display = 'none';
                    this.renderResult(data.result);
                }, 500);
            } else if (data.status === 'error') {
                this.statusEl.textContent = 'Error: ' + data.error;
                this.progressBar.style.display = 'none';
            }
        } catch (err: any) {
            console.error(err);
            setTimeout(() => this.pollStatus(), 3000); // retry on fetch error
        }
    }

    renderResult(result: any) {
        if (!result || !result.text) {
            this.statusEl.textContent = 'Error: No transcript found.';
            return;
        }

        const transcriptBox = document.createElement('div');
        transcriptBox.className = 'transcript-box';
        transcriptBox.textContent = result.text;
        
        const copyBtn = document.createElement('button');
        copyBtn.className = 'download-btn';
        copyBtn.innerHTML = '<i class="fas fa-copy"></i> Copy Text';
        copyBtn.onclick = () => {
            navigator.clipboard.writeText(result.text);
            copyBtn.innerHTML = '<i class="fas fa-check"></i> Copied!';
            setTimeout(() => copyBtn.innerHTML = '<i class="fas fa-copy"></i> Copy Text', 2000);
        };
        
        const txtBlob = new Blob([result.text], { type: 'text/plain' });
        const txtUrl = URL.createObjectURL(txtBlob);
        const downloadBtn = document.createElement('a');
        downloadBtn.className = 'download-btn';
        downloadBtn.href = txtUrl;
        downloadBtn.download = 'transcript.txt';
        downloadBtn.innerHTML = '<i class="fas fa-download"></i> Download .txt';
        
        const btnRow = document.createElement('div');
        btnRow.style.display = 'flex';
        btnRow.style.gap = '10px';
        btnRow.appendChild(copyBtn);
        btnRow.appendChild(downloadBtn);
        
        this.actionArea.appendChild(transcriptBox);
        this.actionArea.appendChild(btnRow);
    }
}

export function initJustRecordBridge() {
    if (!customElements.get('justrecord-widget')) {
        customElements.define('justrecord-widget', JustRecordWidget);
    }
}
